import { useEffect, useRef, useState } from "react";
import { ApiError } from "../../shared/api/client";
import { sameTerminalPlanRevision, sameTerminalWorkOrderNumber } from "../../../shared/production/terminal-work-orders";
import type { ProductionRun, TerminalBootstrap } from "./models";
import { productionGateway, type OpenTerminalWorkOrder, type DirectTerminalWorkOrder, type PlanTerminalWorkOrderSuggestion } from "./production-gateway";
import { OperatorWorkOrderRequestController } from "./operator-work-order-request-controller";

type SourceMode = "PLANNED" | "DIRECT";
type WorkOrderPreview = (OpenTerminalWorkOrder & { sourceMode: "PLANNED" }) | (DirectTerminalWorkOrder & { sourceMode: "DIRECT" });

export function useOperatorWorkOrdersWorkspace(
  context: TerminalBootstrap,
  onStarted: (run: ProductionRun, replayed: boolean) => void,
  onReconcile: () => Promise<ProductionRun | null>,
) {
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState<WorkOrderPreview | null>(null);
  const [sourceMode, setSourceMode] = useState<SourceMode>("PLANNED");
  const [directCandidates, setDirectCandidates] = useState<DirectTerminalWorkOrder[]>([]);
  const [warning, setWarning] = useState<string | null>(null);
  const [weeklyPlan, setWeeklyPlan] = useState<PlanTerminalWorkOrderSuggestion["weeklyPlan"]>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  const [error, setError] = useState("");
  const requests = useRef(new OperatorWorkOrderRequestController());
  const terminalCode = context.terminal.terminalCode;
  useEffect(() => () => { requests.current.cancelLookup(); }, []);

  async function search() {
    if (sourceMode === "DIRECT") await verifyDirect(query.trim());
    else await verify(query.trim());
  }

  async function verify(workOrderNo: string, productCode?: string, selected?: PlanTerminalWorkOrderSuggestion) {
    if (!workOrderNo || requests.current.startBlocked) return;
    const request = requests.current.beginLookup();
    setLoading(true); setError(""); setPreview(null); setWeeklyPlan(null);
    try {
      const { items, warning } = await productionGateway.openTerminalWorkOrders(terminalCode, workOrderNo, request.signal);
      if (!request.isCurrent()) return;
      const exact = items.filter((item) => sameTerminalWorkOrderNumber(item.workOrderNo, workOrderNo) && (productCode === undefined || item.productCode === productCode));
      if (exact.length === 1 && !exact[0].startEligibility.allowed) {
        setError(exact[0].startEligibility.reason ?? "Bu iş emri bu hattın sayaç birimiyle başlatılamaz.");
      } else if (exact.length === 1) {
        if (selected?.weeklyPlan && !sameTerminalPlanRevision(selected.weeklyPlan.revision, exact[0].planRevision)) {
          setError("Hat planı değişti. Güncel listeden yeniden seçin.");
        } else { setPreview({ ...exact[0], sourceMode: "PLANNED" }); setWeeklyPlan(selected?.weeklyPlan ?? null); }
      }
      else if (exact.length > 1) setError("Bu iş emri numarası birden fazla açık kayda karşılık geliyor. Sistem yöneticisine bildirin.");
      else setError(warning ?? "Bu numarayla hat planında açık iş emri bulunamadı. Listeden güncel bir iş seçin.");
    } catch (cause) {
      if (request.isCurrent()) setError(cause instanceof Error ? cause.message : "İş emri doğrulanamadı. Yeniden deneyin.");
    } finally { if (request.isCurrent()) setLoading(false); }
  }

  async function verifyDirect(workOrderNo: string) {
    if (!workOrderNo || requests.current.startBlocked) return;
    const request = requests.current.beginLookup();
    setLoading(true); setError(""); setPreview(null); setWeeklyPlan(null); setDirectCandidates([]); setWarning(null);
    try {
      const result = await productionGateway.directTerminalWorkOrders(terminalCode, workOrderNo, request.signal);
      if (!request.isCurrent()) return;
      const exact = result.items.filter(item => sameTerminalWorkOrderNumber(item.workOrderNo, workOrderNo));
      const keys = exact.map(item => `${item.workOrderNo}:${item.productCode}`);
      if (new Set(keys).size !== keys.length) {
        setError("İş emrinin ürün kaydı belirsiz. Sistem yöneticisine bildirin.");
      } else {
        setDirectCandidates(exact); setWarning(result.warning);
        if (exact.length === 1) setPreview({ ...exact[0], sourceMode: "DIRECT" });
        else if (!exact.length) setError(result.warning ?? "Bu numarayla Mikro'da açık iş emri bulunamadı.");
      }
    } catch (cause) {
      if (request.isCurrent()) setError(cause instanceof Error ? cause.message : "Mikro iş emri doğrulanamadı. Yeniden deneyin.");
    } finally { if (request.isCurrent()) setLoading(false); }
  }
  const sourceNote = preview?.sourceMode === "DIRECT" ? preview.sourceNote : preview?.planNote;

  const startBlockReason = !preview ? null : !preview.startEligibility.allowed ? preview.startEligibility.reason
    : preview.sourceMode === "DIRECT" && (preview.plannedQuantity === null || preview.unit === null)
      ? "İş emrinin miktarı veya birimi doğrulanmadı. Yeniden doğrulayın."
      : preview.sourceMode === "DIRECT" && preview.sourceNoteAvailability === "PRESENT" && !preview.sourceNote?.trim()
        ? "Kaynak notu doğrulanamadı. Yeniden doğrulayın." : null;
  async function start(planNoteAcknowledged: boolean) {
    if (!preview || startBlockReason) return;
    if (sourceNote && !planNoteAcknowledged) {
      setError("İşi başlatmadan önce ürün notunu okuduğunuzu onaylayın.");
      return;
    }
    if (!requests.current.beginStart()) return;
    setSaving(true); setError("");
    let startConfirmed = false;
    try {
      const result = await productionGateway.startTerminalWorkOrder(terminalCode, {
        workOrderNo: preview.workOrderNo,
        productCode: preview.productCode,
        expectedSourceFingerprint: preview.sourceFingerprint,
        expectedPlanRevision: preview.sourceMode === "DIRECT" ? null : preview.planRevision,
        ...(preview.sourceMode === "DIRECT" ? { sourceMode: "DIRECT" as const } : {}),
        planNoteAcknowledged,
      });
      startConfirmed = true;
      onStarted(result.run, result.replayed);
    } catch (cause) {
      if (startConfirmed) {
        setError("İş emri başlatıldı; ekran güncellenemedi. Açık işi yenileyin.");
      } else if (cause instanceof ApiError && cause.code === "OPERATION_OUTCOME_UNKNOWN") {
        requests.current.markOutcomeUnknown(); setOutcomeUnknown(true);
        await reconcile();
      } else if (cause instanceof ApiError && cause.code === "WORK_ORDER_SOURCE_CHANGED") {
        setPreview(null); setDirectCandidates([]);
        setError("İş emrinin ürün, miktar, birim veya notu değişti. Güncel bilgileri yeniden doğrulayın.");
      } else setError(cause instanceof Error ? cause.message : "İş emri başlatılamadı. Açık işi kontrol edip yeniden deneyin.");
    } finally { requests.current.finishStart(); setSaving(false); }
  }
  return {
    query, preview, weeklyPlan, loading, saving, outcomeUnknown, error, search, start, reconcile,
    sourceMode, directCandidates, warning, sourceNote, startBlockReason,
    setSourceMode(mode: SourceMode) {
      if (requests.current.startBlocked || mode === sourceMode) return;
      requests.current.cancelLookup(); setSourceMode(mode); setQuery(""); setPreview(null); setWeeklyPlan(null);
      setDirectCandidates([]); setWarning(null); setError(""); setLoading(false);
    },
    selectDirect(item: DirectTerminalWorkOrder) {
      if (requests.current.startBlocked || sourceMode !== "DIRECT" || loading || !directCandidates.includes(item)) return;
      setPreview({ ...item, sourceMode: "DIRECT" }); setError("");
    },
    setQuery(value: string) {
      if (requests.current.startBlocked) return;
      requests.current.cancelLookup(); setQuery(value); setPreview(null); setWeeklyPlan(null); setError("");
      setDirectCandidates([]); setWarning(null); setLoading(false);
    },
    async selectSuggestion(item: PlanTerminalWorkOrderSuggestion) {
      if (requests.current.startBlocked || loading || sourceMode !== "PLANNED") return;
      setQuery(item.workOrderNo);
      await verify(item.workOrderNo, item.productCode, item);
    },
  };

  async function reconcile() {
    try {
      const active = await onReconcile();
      requests.current.resolveOutcome(); setOutcomeUnknown(false);
      if (active) onStarted(active, true);
      else setError("Sunucuda şu anda açık iş bulunmuyor. Aynı iş emrini yeniden kontrol ederek deneyebilirsiniz.");
      return active;
    } catch (reconcileCause) {
      setError(reconcileCause instanceof Error ? reconcileCause.message : "Başlatma sonucu belirsiz. Açık iş doğrulanana kadar yeniden denemeyin.");
      return null;
    }
  }
}
